import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ScannerTypeDTO, CreateScannerTypeDTO, UpdateScannerTypeDTO, ScannerTypeTicketDTO,
} from '../../models/scanner-type';
import { Response } from '../../models/shared';
import baseQueryWithReauth from './base';

export const scannerTypesApi = createApi({
  reducerPath: 'scannerTypesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['scanner_types', 'scanner_type_tickets'],
  endpoints: (build) => ({
    getScannerTypes: build.query<ScannerTypeDTO[] | null, { businessId: string; eventId: string }>({
      providesTags: () => [{ type: 'scanner_types' }],
      query: ({ businessId, eventId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ScannerTypeDTO[]>): ScannerTypeDTO[] | null => response.data || null,
    }),
    getScannerTypeById: build.query<ScannerTypeDTO | null, { businessId: string; eventId: string; scannerTypeId: string }>({
      providesTags: (result, error, { scannerTypeId }) => [{ type: 'scanner_types', id: scannerTypeId }],
      query: ({ businessId, eventId, scannerTypeId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types/${scannerTypeId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ScannerTypeDTO>): ScannerTypeDTO | null => response.data || null,
    }),
    createScannerType: build.mutation<Response<ScannerTypeDTO>, { businessId: string; eventId: string; data: CreateScannerTypeDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['scanner_types'],
    }),
    updateScannerType: build.mutation<Response<ScannerTypeDTO>, { businessId: string; eventId: string; data: UpdateScannerTypeDTO }>({
      query: ({ businessId, eventId, data }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['scanner_types'],
    }),
    deleteScannerType: build.mutation<Response<ScannerTypeDTO>, { businessId: string; eventId: string; scannerTypeId: string }>({
      query: ({ businessId, eventId, scannerTypeId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types/${scannerTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['scanner_types'],
    }),
    getScannerTypeTickets: build.query<ScannerTypeTicketDTO[] | null, { businessId: string; eventId: string; scannerTypeId: string }>({
      providesTags: (result, error, { scannerTypeId }) => [{ type: 'scanner_type_tickets', id: scannerTypeId }],
      query: ({ businessId, eventId, scannerTypeId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types/${scannerTypeId}/tickets`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ScannerTypeTicketDTO[]>): ScannerTypeTicketDTO[] | null => response.data || null,
    }),
    addTicketToScannerType: build.mutation<Response<ScannerTypeTicketDTO>, { businessId: string; eventId: string; scannerTypeId: string; ticketId: string }>({
      query: ({
        businessId, eventId, scannerTypeId, ticketId,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types/${scannerTypeId}/tickets`,
        method: 'POST',
        body: { ticket_id: ticketId },
      }),
      invalidatesTags: ['scanner_type_tickets'],
    }),
    removeTicketFromScannerType: build.mutation<Response<ScannerTypeTicketDTO>, { businessId: string; eventId: string; scannerTypeId: string; scannerTypeTicketId: string }>({
      query: ({
        businessId, eventId, scannerTypeId, scannerTypeTicketId,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-types/${scannerTypeId}/tickets/${scannerTypeTicketId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['scanner_type_tickets'],
    }),
  }),
});

export const {
  useGetScannerTypesQuery,
  useGetScannerTypeByIdQuery,
  useCreateScannerTypeMutation,
  useUpdateScannerTypeMutation,
  useDeleteScannerTypeMutation,
  useGetScannerTypeTicketsQuery,
  useAddTicketToScannerTypeMutation,
  useRemoveTicketFromScannerTypeMutation,
} = scannerTypesApi;
